import { getPayments } from '@paintscout/util/builder'
import type { Payment, QuoteDocument } from 'paintscout'

/**
 * Statuses that mean "money actually moved (or came back) on the server". A local `pending` request
 * that the customer paid in the portal lands here; a local payment that merely failed does not, so
 * we don't nag the estimator about state they already caused.
 */
const MEANINGFUL_STATUSES: Payment['status'][] = ['paid', 'processing', 'refunded']

export interface NewPaymentActivity {
  /** Payments on the server the local doc has never seen. */
  added: Payment[]
  /** Payments the local doc has, whose server status moved to paid/processing/refunded. */
  statusChanged: Payment[]
  /**
   * A deposit taken on the child invoice that this copy doesn't know about. Deposits live on the
   * invoice, so a parent estimate only learns about them through its `depositInfo` mirror.
   */
  deposit?: Payment
  hasNewActivity: boolean
}

const EMPTY: NewPaymentActivity = { added: [], statusChanged: [], hasNewActivity: false }

/**
 * Diff the payments on a freshly-fetched quote against the copy the estimator has open, so we can
 * refuse to record a payment on top of one someone else (customer view, another estimator) already
 * recorded — the duplicate-payment report in #21913.
 *
 * Deliberately conservative in both directions:
 * - Fails open when there is no fresh doc (no refetch available, or the fetch failed) — a network
 *   blip must never block a legitimate payment.
 * - Ignores payments that exist locally but not on the server: that's the estimator's own unsaved
 *   work, not remote activity.
 */
export function findNewPaymentActivity({
  current,
  fresh
}: {
  current: QuoteDocument
  fresh?: QuoteDocument | null
}): NewPaymentActivity {
  if (!fresh || !current) {
    return EMPTY
  }

  const localById = new Map<string, Payment>()
  getPayments({ quote: current }).forEach((payment) => {
    if (payment?.id) {
      localById.set(payment.id, payment)
    }
  })

  const added: Payment[] = []
  const statusChanged: Payment[] = []

  getPayments({ quote: fresh }).forEach((payment) => {
    // Payments without an id can't be matched to a local counterpart — treating them as new would
    // fire the warning on every check.
    if (!payment?.id) {
      return
    }
    const local = localById.get(payment.id)
    if (!local) {
      added.push(payment)
    } else if (local.status !== payment.status && MEANINGFUL_STATUSES.includes(payment.status)) {
      statusChanged.push(payment)
    }
  })

  // Deposits are written to the child invoice, so on a parent estimate `payments[]` never sees them
  // — depositInfo (a copy of the one deposit payment) is the only signal the estimate carries.
  const freshDeposit = fresh.depositInfo
  const deposit =
    freshDeposit?.id && freshDeposit.id !== current.depositInfo?.id && !localById.has(freshDeposit.id)
      ? freshDeposit
      : undefined

  return {
    added,
    statusChanged,
    deposit,
    hasNewActivity: added.length > 0 || statusChanged.length > 0 || !!deposit
  }
}
